import type { OrgDoc, Role } from './types'

/**
 * The role -> capability matrix, in one place.
 *
 * This is the UI copy. The enforcement copy is firebase/firestore.rules, which
 * cannot import this file because rules are their own language. Drift between
 * the two is a security bug rather than a cosmetic one, so firebase/rules.test.ts
 * asserts every cell against the emulator. Change all three together.
 *
 * Resolving capabilities through this table rather than scattering
 * `if (role === 'owner')` checks is what makes the unresolved question below
 * cheap to answer later.
 */
export type Capability =
  // read
  | 'dashboard.read'
  | 'shareLink.seeBadge'
  // dashboards
  | 'dashboard.create'
  | 'dashboard.edit'
  | 'dashboard.softDelete'
  | 'dashboard.restore'
  | 'dashboard.setVisibility'
  // sharing
  | 'shareLink.create'
  | 'shareLink.readToken'
  | 'shareLink.revoke'
  // organisation
  | 'member.invite'
  | 'member.remove'
  | 'member.setRole'
  | 'org.rename'
  | 'org.delete'
  // later layers
  | 'capture.start'

const VIEWER: readonly Capability[] = [
  'dashboard.read',
  // Everyone sees THAT a dashboard is link-shared, because once the team layer
  // holds notes and strategy, anyone about to type into a dashboard needs to
  // know whether it is publicly linkable. Only editors see the token itself.
  'shareLink.seeBadge',
]

const EDITOR: readonly Capability[] = [
  ...VIEWER,
  'dashboard.create',
  'dashboard.edit',
  'dashboard.softDelete',
  'dashboard.restore',
  'dashboard.setVisibility',
  'shareLink.create',
  'shareLink.readToken',
  'shareLink.revoke',

  // UNRESOLVED (design brief, open question 4 follow-up): starting a capture is
  // the expensive, rate-limited, externally-visible action. Editor is
  // operationally right — the engineer at the track needs to start it — but it
  // hands a billable action to a casually-granted role. Parked here so that
  // moving it, or adding a per-member override, is an edit to this table rather
  // than a refactor across every security rule. Not yet implemented anywhere.
  'capture.start',
]

const OWNER: readonly Capability[] = [
  ...EDITOR,
  'member.invite',
  'member.remove',
  'member.setRole',
  'org.rename',
  'org.delete',
]

export const ROLE_CAPABILITIES: Readonly<Record<Role, readonly Capability[]>> = {
  viewer: VIEWER,
  editor: EDITOR,
  owner: OWNER,
}

/** Role-derived permission check. Returns false for non-members (`null` role). */
export function can(role: Role | null | undefined, capability: Capability): boolean {
  if (!role) return false
  return ROLE_CAPABILITIES[role].includes(capability)
}

/**
 * Transferring primary ownership is not role-derived — it is reserved to the
 * one account that currently holds it, so it deliberately sits outside `can()`.
 */
export function canTransferPrimaryOwnership(
  org: Pick<OrgDoc, 'primaryOwnerUid'>,
  uid: string,
): boolean {
  return org.primaryOwnerUid === uid
}

/**
 * The primary owner is structurally un-removable; they must transfer primary
 * ownership first. Mirrors the delete condition in firestore.rules.
 */
export function canRemoveMember(
  actorRole: Role | null | undefined,
  org: Pick<OrgDoc, 'primaryOwnerUid'>,
  targetUid: string,
): boolean {
  return can(actorRole, 'member.remove') && org.primaryOwnerUid !== targetUid
}
